export const COPY_LINK_LANGUAGES = [
  "en",
  "pl",
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "nl",
  "ja",
  "ko",
  "zh",
] as const;

export type CopyLinkLanguage = (typeof COPY_LINK_LANGUAGES)[number];

export const COPY_LINK_LANGUAGE_LABELS: Record<CopyLinkLanguage, string> = {
  en: "English",
  pl: "Polski",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  pt: "Português",
  nl: "Nederlands",
  ja: "日本語",
  ko: "한국어",
  zh: "中文",
};

export type CopyLinkCopy = {
  title: string;
  description: string;
  copyAndInstall: string;
  copied: string;
  installNow: string;
  footerPrefix: string;
};

export const COPY_LINK_TRANSLATIONS: Record<CopyLinkLanguage, CopyLinkCopy> = {
  en: {
    title: "Continue in the app",
    description:
      "Install the app to continue. Copy the link first, and it'll take you straight to where you were headed as soon as the app opens.",
    copyAndInstall: "Copy link, then install",
    copied: "Copied, opening store...",
    installNow: "Install app now",
    footerPrefix: "Link handled by",
  },
  pl: {
    title: "Kontynuuj w aplikacji",
    description:
      "Zainstaluj aplikację, aby kontynuować. Najpierw skopiuj link, a aplikacja od razu przeniesie Cię tam, dokąd miał prowadzić.",
    copyAndInstall: "Skopiuj link i zainstaluj",
    copied: "Skopiowano, otwieram sklep...",
    installNow: "Zainstaluj aplikację",
    footerPrefix: "Link obsługiwany przez",
  },
  es: {
    title: "Continuar en la app",
    description:
      "Instala la app para continuar. Copia el enlace primero y, en cuanto abras la app, te llevará directo a donde debía llevarte.",
    copyAndInstall: "Copiar enlace e instalar",
    copied: "Copiado, abriendo la tienda...",
    installNow: "Instalar la app ahora",
    footerPrefix: "Enlace gestionado por",
  },
  fr: {
    title: "Continuer dans l'application",
    description:
      "Installez l'application pour continuer. Copiez d'abord le lien : dès que l'application s'ouvrira, elle vous mènera directement là où ce lien devait vous conduire.",
    copyAndInstall: "Copier le lien, puis installer",
    copied: "Copié, ouverture du store...",
    installNow: "Installer l'application",
    footerPrefix: "Lien géré par",
  },
  de: {
    title: "In der App fortfahren",
    description:
      "Installiere die App, um fortzufahren. Kopiere zuerst den Link – sobald die App geöffnet ist, führt sie dich direkt dorthin, wohin der Link führen sollte.",
    copyAndInstall: "Link kopieren und installieren",
    copied: "Kopiert, Store wird geöffnet...",
    installNow: "App jetzt installieren",
    footerPrefix: "Link bereitgestellt von",
  },
  it: {
    title: "Continua nell'app",
    description:
      "Installa l'app per continuare. Copia prima il link: appena apri l'app, ti porterà direttamente dove il link doveva condurti.",
    copyAndInstall: "Copia link e installa",
    copied: "Copiato, apertura dello store...",
    installNow: "Installa l'app ora",
    footerPrefix: "Link gestito da",
  },
  pt: {
    title: "Continuar no app",
    description:
      "Instale o app para continuar. Copie o link primeiro; assim que o app abrir, ele vai te levar direto para onde o link deveria te levar.",
    copyAndInstall: "Copiar link e instalar",
    copied: "Copiado, abrindo a loja...",
    installNow: "Instalar o app agora",
    footerPrefix: "Link gerenciado por",
  },
  nl: {
    title: "Doorgaan in de app",
    description:
      "Installeer de app om door te gaan. Kopieer eerst de link — zodra de app opent, word je direct naar de juiste plek gebracht.",
    copyAndInstall: "Link kopiëren en installeren",
    copied: "Gekopieerd, winkel wordt geopend...",
    installNow: "App nu installeren",
    footerPrefix: "Link beheerd door",
  },
  ja: {
    title: "アプリで続ける",
    description:
      "続けるにはアプリをインストールしてください。先にリンクをコピーしておくと、アプリを開いたときにこのリンクの行き先まで自動的に案内されます。",
    copyAndInstall: "リンクをコピーしてインストール",
    copied: "コピーしました。ストアを開いています…",
    installNow: "今すぐアプリをインストール",
    footerPrefix: "リンク提供:",
  },
  ko: {
    title: "앱에서 계속하기",
    description:
      "계속하려면 앱을 설치하세요. 링크를 먼저 복사해두면 앱을 열었을 때 이 링크가 원래 이동하려던 곳으로 바로 안내해 줍니다.",
    copyAndInstall: "링크 복사 후 설치",
    copied: "복사됨, 스토어를 여는 중...",
    installNow: "지금 앱 설치",
    footerPrefix: "링크 제공:",
  },
  zh: {
    title: "在应用中继续",
    description:
      "安装应用以继续。先复制此链接，打开应用后会自动带你前往该链接原本要去的地方。",
    copyAndInstall: "复制链接并安装",
    copied: "已复制，正在打开商店…",
    installNow: "立即安装应用",
    footerPrefix: "链接由以下提供:",
  },
};

export function isCopyLinkLanguage(value: unknown): value is CopyLinkLanguage {
  return (
    typeof value === "string" &&
    (COPY_LINK_LANGUAGES as readonly string[]).includes(value)
  );
}

export function getCopyLinkCopy(
  language: string | null | undefined
): CopyLinkCopy {
  if (isCopyLinkLanguage(language)) return COPY_LINK_TRANSLATIONS[language];
  return COPY_LINK_TRANSLATIONS.en;
}
